import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../utils/Api";
import {
	Typography,
	Flex,
	Table,
	Statistic,
	Row,
	Col,
	Button,
	Form,
	InputNumber,
	Modal,
} from "antd";
import Layout from "../layout/Layout";
import { CustomSearch, CustomSpinner } from "../utils/CustomComponents";
import dayjs from "dayjs";
import { CSVLink } from "react-csv";
import AssignRatePeerLoadModalByClient from "./AssignRatePeerLoadModalByClient";
import AssignRatePeerLoadModalToday from "./AssignRatePeerLoadModalToday";
const { Text } = Typography;

const columnsCSV = [
	{ key: "date", label: "Date" },
	{ key: "truck", label: "Truck" },
	{ key: "driver", label: "Driver" },
	{ key: "ticket", label: "Ticket" },
	{ key: "origin", label: "Origin" },
	{ key: "destination", label: "Destination" },
	{ key: "material", label: "Material" },
	{ key: "loads", label: "Loads" },
	{ key: "quantity", label: "Quantity" },
	{ key: "rateLoad", label: "Driver Pay" },
	{ key: "total", label: "Total" },
];

const LogisticsReportRatePerLoad = () => {
	const [loading, setLoading] = useState(true);
	const [loadingTable, setLoadingTable] = useState(false);
	const [startDate, setStartDate] = useState(dayjs());
	const [endDate, setEndDate] = useState(dayjs());
	const [list, setList] = useState([]);
	const [listCSV, setListCSV] = useState([]);
	const [openModalRatePeerLoadsClient, setOpenModalRatePeerLoadsClient] =
		useState(false);
	const [openModalRatePeerLoadsToday, setOpenModalRatePeerLoadsToday] =
		useState(false);
	const [expandedRowKeys, setExpandedRowKeys] = useState([]);
	const refDownload = useRef();

	/**
	 ** Options header (columns)
	 */
	const columns = [
		{
			title: "Driver",
			dataIndex: "driver",
			key: "driver",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Trips #",
			dataIndex: "count",
			key: "count",
			render: (value) => {
				return (
					<Statistic
						title=""
						value={value}
						precision={0}
						valueStyle={{ fontSize: 14 }}
					/>
				);
			},
		},
	];

	const EditableCell = ({
		editMode,
		dataIndex,
		record,
		title,
		handleSave,
		children,
		...restProps
	}) => {
		const [form] = Form.useForm();
		return (
			<td {...restProps}>
				{editMode ? (
					<Form form={form} initialValues={{ [dataIndex]: record[dataIndex] }}>
						<Form.Item
							style={{ margin: 0 }}
							name={dataIndex}
							rules={[{ required: false }]}
						>
							<InputNumber
								style={{ width: "100%" }}
								formatter={(value) =>
									`${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, "")
								}
								parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
								onPressEnter={(e) =>
									handleSave({
										...record,
										[dataIndex]: parseFloat(e.target.value) || 0,
									})
								}
								onBlur={(e) =>
									handleSave({
										...record,
										[dataIndex]: parseFloat(e.target.value) || 0,
									})
								}
							/>
						</Form.Item>
					</Form>
				) : (
					children
				)}
			</td>
		);
	};

	const expandedRowRender = (row) => {
		const handleSave = (updatedTrip) => {
			updateTrip(updatedTrip._id, updatedTrip.rateLoad);

			const updatedList = list.map((item) => {
				if (item.key !== row.key) return item;
				const updatedTrips = item.trips.map((trip) =>
					trip.key === updatedTrip.key
						? { ...updatedTrip, total: updatedTrip.rateLoad }
						: trip
				);
				return { ...item, trips: updatedTrips };
			});
			setList(updatedList);
			setExpandedRowKeys((prev) => {
				if (!prev.includes(row.key)) return [...prev, row.key];
				return prev;
			});
		};

		const columns = [
			{
				title: "Date",
				dataIndex: "date",
				key: "date",
				render: (value) => {
					return <Text>{value}</Text>;
				},
			},
			{
				title: "Truck",
				dataIndex: "truck",
				key: "truck",
				render: (value) => {
					return <Text>{value.number}</Text>;
				},
			},
			{
				title: "Driver",
				dataIndex: "driver",
				key: "driver",
				render: (value) => {
					return <Text>{value.name}</Text>;
				},
			},
			{
				title: "Ticket",
				dataIndex: "ticket",
				key: "ticket",
				render: (value) => {
					return <Text>{value}</Text>;
				},
			},
			{
				title: "Origin",
				dataIndex: "origin",
				key: "origin",
				render: (value) => {
					return <Text>{value}</Text>;
				},
			},
			{
				title: "Destination",
				dataIndex: "destination",
				key: "destination",
				render: (value) => {
					return <Text>{value.name}</Text>;
				},
			},
			{
				title: "Material",
				dataIndex: "material",
				key: "material",
				render: (value) => {
					return <Text>{value}</Text>;
				},
			},
			{
				title: "Loads",
				dataIndex: "loads",
				key: "loads",
				render: (value) => {
					return <Text>{value}</Text>;
				},
			},
			{
				title: "Quantity",
				dataIndex: "quantity",
				key: "quantity",
				render: (value) => {
					return (
						<Statistic
							title=""
							value={value}
							precision={2}
							valueStyle={{ fontSize: 14 }}
						/>
					);
				},
			},
			{
				title: "Driver Pay",
				dataIndex: "rateLoad",
				key: "rateLoad",
				editable: true,
			},
			{
				title: "Total",
				dataIndex: "total",
				key: "total",
				render: (value) => {
					return (
						<Statistic
							title=""
							value={value}
							precision={2}
							valueStyle={{ fontSize: 14 }}
							prefix={"$"}
						/>
					);
				},
			},
		];

		const mergedColumns = columns.map((col) => {
			if (!col.editable) {
				return col;
			}
			return {
				...col,
				onCell: (record) => ({
					record,
					editMode: true,
					dataIndex: col.dataIndex,
					title: col.title,
					handleSave: handleSave,
				}),
			};
		});

		const trips = row.trips.map((item, index) => {
			item.key = index;
			item.loads = 1;
			item.rateLoad = item.rateLoad || 0;
			item.date = Intl.DateTimeFormat("en-US", {
				month: "numeric",
				year: "numeric",
				day: "numeric",
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
			}).format(new Date(item.date));
			return item;
		});

		return (
			<Table
				columns={mergedColumns}
				dataSource={trips}
				pagination={false}
				components={{
					body: {
						cell: EditableCell,
					},
				}}
				loading={loadingTable}
				summary={() => {
					const totalSum = trips.reduce(
						(acc, trip) => acc + (trip.total || 0),
						0
					);
					return (
						<Table.Summary.Row>
							<Table.Summary.Cell colSpan={9} align="right">
								<strong>Total:</strong>
							</Table.Summary.Cell>
							<Table.Summary.Cell>
								<Statistic
									value={totalSum}
									precision={2}
									valueStyle={{ fontSize: 14 }}
									prefix={"$"}
								/>
							</Table.Summary.Cell>
						</Table.Summary.Row>
					);
				}}
			/>
		);
	};

	const updateTrip = async (id, rateLoad) => {
		setLoadingTable(true);
		const url =
			api +
			`/logisticstrip/update/rate/${id}?rateLoad=${rateLoad}&total=${rateLoad}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.post(url);
			if (data.success) {
				const index = listCSV.findIndex((item) => item._id === id);
				if (index !== -1) {
					let updatedList = [...listCSV];
					updatedList[index] = {
						...updatedList[index],
						rateLoad: rateLoad,
						total: rateLoad,
					};
					setListCSV(updatedList);
				}
				setLoadingTable(false);
			} else {
				setLoadingTable(true);
			}
		} catch (err) {
			setLoadingTable(false);
			console.error(err.message);
			return null;
		}
	};

	useEffect(() => {
		getLogisticsList();
	}, [startDate, endDate]);

	const getLogisticsList = async () => {
		setLoading(true);
		setListCSV([]);

		const start = `${startDate.year()}-${
			startDate.month() + 1
		}-${startDate.date()}`;
		const end = `${endDate.year()}-${endDate.month() + 1}-${endDate.date()}`;

		const url =
			api +
			`/logisticstrip/list/tripsperdriver?startDate=${start}&endDate=${end}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			let list = [];
			let listRows = [];
			data.map((item, index) => {
				list.push({
					key: index,
					id: item._id,
					driver: item.driver_details.name,
					count: item.count,
					trips: item.trips,
				});
				item.trips.map((trip) => {
					listRows.push({
						_id: trip._id,
						date: Intl.DateTimeFormat("en-US", {
							month: "numeric",
							year: "numeric",
							day: "numeric",
							hour: "2-digit",
							minute: "2-digit",
							second: "2-digit",
						}).format(new Date(trip.date)),
						driver: trip.driver.name,
						truck: trip.truck.number,
						quantity: trip.quantity,
						material: trip.material,
						ticket: trip.ticket,
						origin: trip.origin,
						destination: trip.destination.name,
						rateLoad: item.rateLoad || 0,
						total: trip.total || 0,
						loads: 1,
					});
				});
			});
			list = list.sort((a, b) => a.driver - b.driver);
			setList(list);
			setListCSV(listRows);
			setLoading(false);
		} catch (err) {
			console.error(err.message);
			return null;
		}
	};

	const RenderTable = () => {
		return (
			<Flex gap={"middle"} vertical>
				<Table
					columns={columns}
					dataSource={list}
					expandable={{
						expandedRowRender,
						expandedRowKeys: expandedRowKeys,
						onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
					}}
					pagination={{
						position: ["bottomRight"],
						pageSizeOptions: [10, 20, 50, 100],
						defaultPageSize: 10,
						showSizeChanger: true,
					}}
					scroll={{
						x: true,
					}}
					style={{
						minWidth: "100%",
					}}
				/>
			</Flex>
		);
	};

	const onRangeChange = (dates) => {
		if (dates) {
			setStartDate(dates[0]);
			setEndDate(dates[1]);
		}
	};

	const openModalClient = () => {
		setOpenModalRatePeerLoadsClient(true);
	};

	// const openModalToday = () => {
	// 	setOpenModalRatePeerLoadsToday(true);
	// };

	const RenderDates = () => {
		return (
			<Row gutter={16}>
				<Col>
					<CustomSearch
						loading={loading}
						startDate={startDate}
						endDate={endDate}
						dateFormat={"MM/DD/YYYY"}
						onRangeChange={onRangeChange}
					/>
				</Col>
				{/* <Col>
					<Button
						size="large"
						type="primary"
						onClick={openModalToday}
						loading={loading}
						disabled={loading}
					>
						Apply Rate Per Loads Today
					</Button>
				</Col> */}
				<Col>
					<Button
						size="large"
						type="primary"
						onClick={openModalClient}
						loading={loading}
						disabled={loading}
					>
						Apply Rate Per Loads by Route
					</Button>
				</Col>
			</Row>
		);
	};

	const RenderDownloadReport = () => {
		return (
			<div>
				<CSVLink
					data={listCSV}
					headers={columnsCSV}
					filename={"57concrete-logistics-rate-per-load.csv"}
					hidden
					ref={refDownload}
				>
					Download report
				</CSVLink>
				<Row gutter={16}>
					<Col span={24}>
						<Button
							type="primary"
							style={{ width: "100%" }}
							onClick={(e) => refDownload.current.link.click()}
							loading={loading}
							size="large"
						>
							Download Report
						</Button>
					</Col>
				</Row>
			</div>
		);
	};

	const onCompleteUpdateRateLoadByClient = useCallback(async () => {
		setOpenModalRatePeerLoadsClient(false);
		await getLogisticsList();
	}, [setOpenModalRatePeerLoadsClient]);

	const ModalRatePeerLoadsByClient = () => {
		return (
			<Modal
				title={`Assign Rate Peer Load by Route`}
				open={openModalRatePeerLoadsClient}
				onCancel={() => {
					setOpenModalRatePeerLoadsClient(false);
				}}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				width={1000}
				centered
			>
				<AssignRatePeerLoadModalByClient
					onComplete={onCompleteUpdateRateLoadByClient}
					// startDate={startDate}
					// endDate={endDate}
				/>
			</Modal>
		);
	};

	// const onCompleteUpdateRateLoadToday = useCallback(async () => {
	// 	setOpenModalRatePeerLoadsToday(false);
	// 	await getLogisticsList();
	// }, [setOpenModalRatePeerLoadsToday]);

	// const ModalRatePeerLoadsToday = () => {
	// 	return (
	// 		<Modal
	// 			title={`Assign Rate Peer Load Today`}
	// 			open={openModalRatePeerLoadsToday}
	// 			onCancel={() => {
	// 				setOpenModalRatePeerLoadsToday(false);
	// 			}}
	// 			cancelButtonProps={{ style: { display: "none" } }}
	// 			okButtonProps={{ style: { display: "none" } }}
	// 			width={1000}
	// 			centered
	// 		>
	// 			<AssignRatePeerLoadModalToday
	// 				onComplete={onCompleteUpdateRateLoadToday}
	// 			/>
	// 		</Modal>
	// 	);
	// };

	return (
		<Layout title="Rate Per Loads" description="Logistics Report" type="medium">
			<Flex vertical gap={"large"}>
				<RenderDates />
				<RenderDownloadReport />
				{loading ? <CustomSpinner /> : <RenderTable />}
				<ModalRatePeerLoadsByClient />
				{/* <ModalRatePeerLoadsToday /> */}
			</Flex>
		</Layout>
	);
};

export default LogisticsReportRatePerLoad;
